import React, { useCallback, useMemo, useState } from 'react';
import { depositSubmit } from 'helpers/depositSubmit';
import PropTypes from 'prop-types';

import {
  constants,
  DateService,
  IntlManager,
  NotificationManager,
  PaymentV2Api,
} from '@atlas/core';
import { Button, Icon, Label, Loader } from '@atlas/ui';

import { transactionStatuses } from 'common/constants';
import ModalPortal from 'common/ModalPortal/ModalPortal';
import ConfirmPopup from 'common/Popup/ConfirmPopup';
import { getReadableString } from 'common/utils/utils';
import PaymentIframe from 'components/ClientProfile/Payment/Deposit/PaymentIframe';
import TransactionCancelButton from 'components/ClientProfile/Payment/Transaction/TransactionCancelButton';

const { COMPLETED, FAILED, PENDING, SUCCESS } = transactionStatuses;

const showAtlasTransferProof = publicPaymentId => {
  return NotificationManager.display({
    data: { openSecondStep: true, publicPaymentId },
    message: 'atlasTransfer',
    type: 'atlasTransfer',
  });
};

const onDepositStatusError = () => {
  NotificationManager.display({
    data: {
      headerTitle: 'deposit',
      text: 'deposit_failed_message',
      title: 'deposit_failed_title',
      withHeader: false,
    },
    type: 'error',
  });
};

const TransactionHistoryTableItem = ({ paymentMethods, transaction }) => {
  const {
    amount,
    cancellationAvailable,
    comment,
    createDate,
    currency,
    description,
    displayPaymentProofButton,
    id,
    operationType,
    paymentMethodName,
    publicId,
    retail,
    status,
  } = transaction;

  const [showDetailCancel, setShowDetailCancel] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState();
  const [isDepositLoading, setDepositLoading] = useState(null);

  const depositCompleteHandler = useCallback(
    async publicPaymentId => {
      if (!publicPaymentId || isDepositLoading) return;
      setDepositLoading(true);

      PaymentV2Api.checkPaymentStatusWithRetry({ onDepositStatusError, publicPaymentId })
        .then(data => {
          const selectedPaymentForm = paymentMethods.find(pm => pm.id === data.pmId);
          const paymentMethod = selectedPaymentForm
            ? {
                desktopImageLink: selectedPaymentForm.desktopImageLink,
                name: selectedPaymentForm.paymentMethodName,
                paymentForms:
                  selectedPaymentForm.paymentProperties[constants.PAYMENT_OPERATION.DEPOSIT]
                    .paymentForms,
              }
            : {};

          depositSubmit(data, paymentMethod, setPaymentUrl, amount);
        })
        .catch(() => onDepositStatusError())
        .finally(() => setDepositLoading(null));
    },
    [isDepositLoading, amount, paymentMethods],
  );

  const handleDetailCancel = useCallback(() => {
    setShowDetailCancel(showDetailCancel => !showDetailCancel);
  }, [setShowDetailCancel]);

  const sign = ['DEPOSIT', 'CORRECTION_BALANCE_DEPOSIT'].includes(operationType) ? '+' : '-';

  const formattedDate = DateService.getFormattedDateWithTime(createDate);

  const statusBlock = useMemo(() => {
    if (status === SUCCESS) {
      return (
        <span className="transaction-status-label">
          <span className="sub-title-xsmall-300-text">
            <Label message="Status" />:
          </span>
          <Label className="sub-title-small-300-text" message={status.toLowerCase()} />
        </span>
      );
    }
    if (status === COMPLETED) {
      const label = 'transaction_successful';
      return (
        <span className="transaction-status-label">
          <span className="sub-title-xsmall-300-text">
            <Label message="Status" />:
          </span>
          <Label className="sub-title-small-300-text" message={label.toLowerCase()} />
        </span>
      );
    }
    if (status === FAILED) {
      return (
        <>
          <span className="transaction-status-label">
            <Icon className="icon-warning" name="warning" />
            <span className="sub-title-xsmall-300-text">
              <Label message="Status" />:
            </span>
            <span className="sub-title-small-300-text">
              <Label message={status.toLowerCase()} />.
            </span>
          </span>
        </>
      );
    }
    if (status === PENDING) {
      return (
        <span className="transaction-status-label">
          <Icon className="icon-in-procces" name="in-procces" />
          <span className="sub-title-xsmall-300-text">
            <Label message="Status" />:
          </span>
          <Label className="sub-title-small-300-text" message="payment_history_processing" />
        </span>
      );
    }
  }, [status]);

  const actionBlock = useMemo(
    () => (
      <>
        {status === FAILED && (
          <Button
            className="btn"
            label="read_more"
            onClick={handleDetailCancel}
            size="medium"
            type="secondary"
          />
        )}
        {status === PENDING && cancellationAvailable && (
          <>
            <TransactionCancelButton publicPaymentId={publicId} transactionId={id} />
            {!displayPaymentProofButton && operationType === 'DEPOSIT' && (
              <>
                <Button
                  className="btn history-table__action"
                  disabled={isDepositLoading}
                  label="deposit_complete_button_text"
                  onClick={() => depositCompleteHandler(publicId)}
                  size="medium"
                  type="primary"
                >
                  {isDepositLoading && <Loader className="btn-loader absolute" />}
                </Button>
                {paymentUrl && (
                  <ModalPortal backdropOff className="casino-game">
                    <PaymentIframe paymentUrl={paymentUrl} setPaymentUrl={setPaymentUrl} />
                  </ModalPortal>
                )}
              </>
            )}
          </>
        )}
        {displayPaymentProofButton && (
          <Button
            className="btn history-table__astion"
            label="atlas_transfer_button_text"
            onClick={() => showAtlasTransferProof(publicId)}
            size="medium"
            type="primary"
          />
        )}
      </>
    ),
    [
      status,
      handleDetailCancel,
      cancellationAvailable,
      id,
      displayPaymentProofButton,
      operationType,
      isDepositLoading,
      paymentUrl,
      depositCompleteHandler,
      publicId,
    ],
  );

  const showWithdrawalCode = useCallback(() => {
    NotificationManager.display({
      data: { id },
      message: 'withdrawalCodePopup',
      type: 'withdrawalCodePopup',
    });
  }, [id]);

  return (
    <>
      <div className="transaction-history-list-item">
        <div className="transaction-history-list-item-info">
          <div className="col">
            {operationType === 'WITHDRAWAL' && retail && (
              <Button
                className="withdrawal-code__button"
                onClick={showWithdrawalCode}
                type="secondary"
              >
                <Label message="withdrawal_code" />
              </Button>
            )}
            <div className="sub-title-xsmall-300-text flex-element">
              <span className="transaction-type">
                <Label message={getReadableString(operationType)} />:
              </span>
              <span className="transaction-id">#{id}</span>
            </div>
            <div className="sub-title-xsmall-300-text flex-element">
              <span>{formattedDate}</span>
            </div>
            <div className="sub-title-xsmall-300-text">
              <span>{paymentMethodName || description}</span>
            </div>
            <div className="transaction-status-block">{statusBlock}</div>
          </div>
          <div className="col">
            <span className="transaction-amount-label">
              {sign}
              {IntlManager.formatMoneyWithCurrency(amount, currency)}
            </span>
          </div>
        </div>
        <div className="transaction-history-list-item-action">{actionBlock}</div>
      </div>
      {showDetailCancel && (
        <ConfirmPopup
          data={{
            className: 'popup__transaction-cancel',
            onConfirm: handleDetailCancel,
            secondaryBtn: false,
          }}
          title="transaction_declined_message"
        >
          <div className="popup__text">{comment}</div>
        </ConfirmPopup>
      )}
    </>
  );
};

TransactionHistoryTableItem.propTypes = {
  paymentMethods: PropTypes.arrayOf(
    PropTypes.shape({
      desktopImageLink: PropTypes.string,
      id: PropTypes.number,
      paymentMethodName: PropTypes.string,
      paymentProofRequirements: PropTypes.object,
      paymentProperties: PropTypes.object,
    }),
  ).isRequired,
  transaction: PropTypes.shape({
    amount: PropTypes.number,
    cancellationAvailable: PropTypes.bool,
    comment: PropTypes.string,
    createDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    currency: PropTypes.string,
    description: PropTypes.string,
    displayPaymentProofButton: PropTypes.bool,
    id: PropTypes.number,
    operationType: PropTypes.string,
    paymentMethodName: PropTypes.string,
    publicId: PropTypes.string,
    retail: PropTypes.bool,
    status: PropTypes.string,
  }).isRequired,
};

export default TransactionHistoryTableItem;
